"use client";

import { useMemo, useRef } from "react";
import * as THREE from "three";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import logoMark from "@/assets/images/logo/forme logo blanc - PNG fond transparent.png";

type BrandSignature3DProps = {
  accentColor?: string;
  className?: string;
};

const OrbitSystem = ({ accentColor }: { accentColor: string }) => {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    groupRef.current.rotation.z += delta * 0.22;
    groupRef.current.rotation.x =
      Math.sin(state.clock.elapsedTime * 0.35) * 0.12;
  });

  const points = useMemo(
    () =>
      Array.from({ length: 8 }, (_, i) => ({
        angle: (Math.PI * 2 * i) / 8,
        radius: i % 2 === 0 ? 2.25 : 1.75,
        size: i % 2 === 0 ? 0.09 : 0.06,
      })),
    [],
  );

  return (
    <group ref={groupRef}>
      <mesh rotation-x={Math.PI / 2}>
        <torusGeometry args={[2.05, 0.035, 16, 120]} />
        <meshStandardMaterial
          color={accentColor}
          metalness={0.75}
          roughness={0.2}
        />
      </mesh>

      <mesh rotation={[Math.PI / 2, 0.45, 0]}>
        <torusGeometry args={[1.25, 0.025, 12, 90]} />
        <meshStandardMaterial color="#ffffff" opacity={0.65} transparent />
      </mesh>

      {points.map((point, index) => (
        <mesh
          key={`${point.angle}-${index}`}
          position={[
            Math.cos(point.angle) * point.radius,
            Math.sin(point.angle) * point.radius,
            0,
          ]}
        >
          <sphereGeometry args={[point.size, 16, 16]} />
          <meshStandardMaterial
            color={accentColor}
            emissive={accentColor}
            emissiveIntensity={0.18}
          />
        </mesh>
      ))}
    </group>
  );
};

const LogoCoin = ({ accentColor }: { accentColor: string }) => {
  const texture = useLoader(THREE.TextureLoader, logoMark.src);
  const coinRef = useRef<THREE.Group>(null);
  const logoTexture = useMemo(() => {
    const clonedTexture = texture.clone();
    clonedTexture.colorSpace = THREE.SRGBColorSpace;
    clonedTexture.needsUpdate = true;
    return clonedTexture;
  }, [texture]);

  useFrame((state, delta) => {
    if (!coinRef.current) return;
    coinRef.current.rotation.y += delta * 0.45;
    coinRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.8) * 0.08;
  });

  return (
    <group ref={coinRef}>
      <mesh>
        <cylinderGeometry args={[0.95, 0.95, 0.2, 64]} />
        <meshStandardMaterial
          color="#0f0f10"
          metalness={0.8}
          roughness={0.25}
        />
      </mesh>

      <mesh position={[0, 0, 0.105]}>
        <planeGeometry args={[1.2, 1.2]} />
        <meshStandardMaterial map={logoTexture} transparent />
      </mesh>

      <mesh position={[0, 0, -0.105]} rotation-y={Math.PI}>
        <planeGeometry args={[1.2, 1.2]} />
        <meshStandardMaterial map={logoTexture} transparent />
      </mesh>

      <pointLight color={accentColor} intensity={2.6} distance={3.5} />
    </group>
  );
};

export default function BrandSignature3D({
  accentColor = "#f3cf86",
  className,
}: BrandSignature3DProps): JSX.Element {
  return (
    <div className={className ?? "h-full w-full"}>
      <Canvas dpr={[1, 2]} camera={{ position: [0, 0, 5.8], fov: 42 }}>
        <color attach="background" args={["#050505"]} />
        <ambientLight intensity={0.55} />
        <directionalLight position={[3, 3, 4]} intensity={1.2} />
        <OrbitSystem accentColor={accentColor} />
        <LogoCoin accentColor={accentColor} />
      </Canvas>
    </div>
  );
}
